import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import paieMg from "../assets/paie-mg.png";
import ImageModal from "../components/ImageModal";

const TechnicalSupportProject = () => {
  const { t } = useLanguage();
  const translations = t("projectPages.technicalSupport");
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  return (
    <section className="py-16 md:py-20 bg-gray-100 dark:bg-gray-800 transition-colors duration-300">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-10 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold mb-3 md:mb-4 dark:text-white">
            {translations.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 max-w-3xl mx-auto text-sm md:text-base">
            {translations.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <motion.div
            className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl font-semibold mb-4 dark:text-white">{t("projectPages.projectOverview")}</h2>
            <p className="text-gray-700 dark:text-gray-300">
              {translations.overview}
            </p>
          </motion.div>

          <motion.div
            className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h2 className="text-xl font-semibold mb-4 dark:text-white">{t("projectPages.skillsDeveloped")}</h2>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300">
              {translations.skills.map((skill, index) => (
                <li key={index} className="mb-2">{skill}</li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h2 className="text-xl font-semibold mb-4 dark:text-white">{t("projectPages.careerBenefits")}</h2>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300">
              {translations.careerBenefits.map((benefit, index) => (
                <li key={index} className="mb-2">
                  <span className="font-medium">{benefit.title}:</span> {benefit.description}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-md mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <h2 className="text-xl font-semibold mb-4 dark:text-white">{t("projectPages.projectDetails")}</h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2 dark:text-gray-200">{t("projectPages.responsibilities")}</h3>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300">
              {translations.responsibilities.map((resp, index) => (
                <li key={index} className="mb-2">{resp}</li>
              ))}
            </ul>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2 dark:text-gray-200">{t("projectPages.challengesSolutions")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {translations.challenges.map((item, index) => (
                <React.Fragment key={index}>
                  <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-md">
                    <h4 className="font-medium mb-1 dark:text-white">{t("projectPages.challenge")}</h4>
                    <p className="text-gray-700 dark:text-gray-300">{item.challenge}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-md">
                    <h4 className="font-medium mb-1 dark:text-white">{t("projectPages.solution")}</h4>
                    <p className="text-gray-700 dark:text-gray-300">{item.solution}</p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-2 dark:text-gray-200">{t("projectPages.keyOutcomes")}</h3>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300">
              {translations.outcomes.map((outcome, index) => (
                <li key={index} className="mb-2">{outcome}</li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div
          className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-md mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h2 className="text-xl font-semibold mb-4 dark:text-white">{t("projectPages.proofOfParticipation")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              className="relative group cursor-pointer"
              onClick={() => handleImageClick(paieMg)}
            >
              <img
                src={paieMg}
                alt="MG2 Payroll System"
                className="w-full h-64 object-cover rounded-lg shadow-md transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 rounded-lg"></div>
            </div>
          </div>
        </motion.div>

        {selectedImage && (
          <ImageModal image={selectedImage} onClose={closeModal} />
        )}

        <motion.div
          className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-md mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h2 className="text-xl font-semibold mb-4 dark:text-white">{t("projectPages.careerPlanConnection")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {translations.careerConnections.map((connection, index) => (
              <div key={index} className={`border-l-4 border-${['blue', 'green', 'purple', 'yellow'][index]}-500 pl-4`}>
                <h3 className="text-lg font-medium mb-2 dark:text-gray-200">{connection.title}</h3>
                <p className="text-gray-700 dark:text-gray-300">{connection.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Link
            to="https://julien.stock4me.com/#education"
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors duration-300"
          >
            {t("projectPages.backToEducation")}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default TechnicalSupportProject;
